import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroServiceCards } from "@/mocks/services";

export default function Hero() {
  const [selected, setSelected] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const active = heroServiceCards.find((c) => c.id === selected) || null;

  useEffect(() => {
    // How much the hero has "contracted" as we scroll. 0 = full-bleed, 1 = framed.
    const DISTANCE = 280;
    const onScroll = () => {
      const p = Math.min(1, Math.max(0, window.scrollY / DISTANCE));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Interpolate from 0 (full bleed) to target values (framed)
  const padX = progress * 16; // 0 -> 16px
  const padTop = progress * 16;
  const radius = progress * 24; // 0 -> 24px

  return (
    <section
      className="relative"
      style={{
        paddingLeft: `${padX}px`,
        paddingRight: `${padX}px`,
        paddingTop: `${padTop}px`,
        transition: "padding 0.15s ease-out",
      }}
    >
      <div
        className="relative w-full min-h-[100svh] md:min-h-[100vh] overflow-hidden"
        style={{
          borderRadius: `${radius}px`,
          transition: "border-radius 0.15s ease-out",
        }}
      >
        <img
          src="https://readdy.ai/api/search-image?query=A%20confident%20independent%20UK%20electrician%20in%20navy%20workwear%20holding%20a%20drill%20standing%20in%20front%20of%20a%20classic%20London%20Islington%20Victorian%20terraced%20brick%20house%20with%20a%20black%20front%20door%20and%20wrought%20iron%20railings%2C%20warm%20golden%20late%20afternoon%20natural%20light%2C%20realistic%20cinematic%20editorial%20photography%2C%20soft%20shadows%2C%20high%20detail%2C%20authentic%20human%20portrait&width=1400&height=900&seq=hero-electrician-main-01&orientation=landscape"
          alt="Independent local electrician standing outside an Islington terraced home"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/40 to-primary-950/80"></div>

        <div className="relative z-10 min-h-[100svh] md:min-h-[100vh] flex flex-col justify-between px-4 sm:px-6 md:px-12 pt-24 md:pt-28 pb-8 md:pb-12 gap-10 sm:gap-14 md:gap-16">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/25 text-background-50 text-xs">
              <span className="relative w-2 h-2 rounded-full bg-emerald-400 pulse-dot text-emerald-400"></span>
              Available this week · Islington &amp; nearby
            </div>
            <div className="hidden md:flex items-center gap-2 text-background-50/85 text-xs">
              <i className="ri-star-fill text-accent-400"></i>
              4.9 · 200+ local reviews
            </div>
          </div>

          <div className="max-w-4xl">
            <h1 className="font-heading font-semibold text-background-50 text-[34px] xs:text-[38px] sm:text-[52px] md:text-[68px] lg:text-[86px] leading-[1.02] tracking-tight">
              Local Electrician<br />
              for <span className="text-accent-400">Homes &amp; Small</span>
              <br className="hidden md:block" /> Businesses in
              <span className="italic font-medium"> North London.</span>
            </h1>
            <p className="mt-4 sm:mt-5 md:mt-7 max-w-xl text-sm sm:text-base md:text-lg text-background-100 leading-relaxed">
              Straightforward electrical repairs, installations and safety checks from
              an independent UK electrician serving the local area.
            </p>
            <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to="/book"
                className="group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-accent-500 text-primary-950 text-sm md:text-base font-semibold hover:bg-accent-600 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
              >
                Book an Appointment
                <i className="ri-arrow-right-up-line text-lg transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
              </Link>
              <Link
                to="/pricing"
                className="group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-background-50/10 border border-background-50/25 text-background-50 text-sm md:text-base font-semibold hover:bg-background-50/20 transition-all cursor-pointer whitespace-nowrap"
              >
                View Quick Pricing
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {heroServiceCards.map((c) => {
                const isActive = selected === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelected(isActive ? null : c.id)}
                    className={`group text-left rounded-2xl p-4 sm:p-5 md:p-6 backdrop-blur-md border transition-all duration-300 cursor-pointer ${
                      isActive
                        ? "bg-accent-500 text-primary-950 border-accent-500 shadow-[0_2px_0_0_rgba(0,0,0,0.06)] -translate-y-0.5"
                        : "bg-background-50/10 border-background-50/25 text-background-50 hover:bg-background-50/15 hover:-translate-y-0.5"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`w-10 h-10 flex items-center justify-center rounded-full ${
                          isActive
                            ? "bg-primary-950 text-accent-400"
                            : "bg-background-50/15 text-background-50"
                        }`}
                      >
                        <i className={`${c.icon} text-lg`}></i>
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full ${
                          isActive ? "bg-primary-950/10 text-primary-950" : "bg-background-50/10 text-background-100"
                        }`}
                      >
                        {c.tag}
                      </span>
                    </div>
                    <h3 className="mt-4 text-lg md:text-xl font-heading font-semibold">
                      {c.title}
                    </h3>
                    <p
                      className={`mt-1 text-sm ${
                        isActive ? "text-primary-900" : "text-background-100/85"
                      }`}
                    >
                      {c.subtitle}
                    </p>
                  </button>
                );
              })}
            </div>

            {active && (
              <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-background-50 fade-up">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 shrink-0">
                    <i className="ri-check-line"></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">
                      {active.title.replace("I need ", "")} — you're covered.
                    </p>
                    <p className="text-sm text-foreground-600">{active.subtitle}</p>
                  </div>
                </div>
                <Link
                  to="/book"
                  className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-950 text-background-50 text-sm font-semibold hover:bg-primary-900 transition-all cursor-pointer whitespace-nowrap"
                >
                  Book this now
                  <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}